import { CommonProps } from '@jdesign-vue/components/cascader-panel'
import { buildProps, definePropType, isBoolean } from '@jdesign-vue/utils'
import { useSizeProp } from '@jdesign-vue/hooks'
import { useTooltipContentProps } from '@jdesign-vue/components/tooltip'
import { tagProps } from '@jdesign-vue/components/tag'
import { CHANGE_EVENT, UPDATE_MODEL_EVENT } from '@jdesign-vue/constants'
import type { JdTooltipProps } from '@jdesign-vue/components/tooltip'
import type {
  CascaderNode,
  CascaderValue,
} from '@jdesign-vue/components/cascader-panel'
import type { Placement } from '@popperjs/core'
import type { PropType } from 'vue'

// 选中项回填的方式
export enum CascaderStrategy {
  // 只显示选中的子节点
  SHOW_CHILD = 'SHOW_CHILD',
  // 只显示父节点（当父节点下所有子节点都选中时）
  SHOW_PARENT = 'SHOW_PARENT',
}

export const cascaderProps = buildProps({
  ...CommonProps,
  /**
   * @description size of input
   */
  size: useSizeProp,
  /**
   * @description placeholder of input
   */
  placeholder: String,
  /**
   * @description whether Cascader is disabled
   */
  disabled: Boolean,
  /**
   * @description whether selected value can be cleared
   */
  clearable: Boolean,
  /**
   * @description whether the options can be searched
   */
  filterable: Boolean,
  /**
   * @description customize search logic, the first parameter is `node`, the second is `keyword`, and need return a boolean value indicating whether it hits.
   */
  filterMethod: {
    type: definePropType<(node: CascaderNode, keyword: string) => boolean>(
      Function
    ),
    default: (node: CascaderNode, keyword: string) =>
      node.suggestionText.includes(keyword),
  },
  /**
   * @description option label separator
   */
  separator: {
    type: String,
    default: ' / ',
  },
  /**
   * @description whether to display all levels of the selected value in the input
   */
  showAllLevels: {
    type: Boolean,
    default: true,
  },
  /**
   * @description whether to collapse tags in multiple selection mode
   */
  collapseTags: Boolean,
  // 折叠选项不换行，仅当collapseTags存在时生效
  collapseTagsNoWrap: {
    type: Boolean,
    default: false,
  },
  /**
   * @description native input id
   */
  collapseTagsTooltip: {
    type: Boolean,
    default: false,
  },
  collapseTagsTooltipProps: {
    type: Object as PropType<JdTooltipProps>,
  },
  /**
   * @description debounce delay when typing filter keyword, in milliseconds
   */
  debounce: {
    type: Number,
    default: 300,
  },
  /**
   * @description hook function before filtering with the value to be filtered as its parameter. If `false` is returned or a `Promise` is returned and then is rejected, filtering will be aborted
   */
  beforeFilter: {
    type: definePropType<(value: string) => boolean | Promise<any>>(Function),
    default: () => true,
  },
  /**
   * @description custom class name for Cascader's dropdown
   */
  popperClass: {
    type: String,
    default: '',
  },
  /**
   * @description whether cascader popup is teleported
   */
  teleported: useTooltipContentProps.teleported,
  /**
   * @description tag type
   */
  // eslint-disable-next-line vue/require-prop-types
  tagType: { ...tagProps.type, default: 'gray' },
  /**
   * @description whether to trigger form validation
   */
  validateEvent: {
    type: Boolean,
    default: true,
  },
  tagsTooltipsMaxWidth: {
    type: String,
    default: '100%',
  },
  tagsTooltipsMaxHeight: {
    type: String,
    default: '400px',
  },
  placement: {
    type: String,
    default: 'bottom-start',
  },
  fallbackPlacements: {
    type: definePropType<Placement[]>(Array),
    default: () => {
      return ['bottom-start', 'bottom', 'top-start', 'top', 'right', 'left']
    },
  },
  /**
   * @description determine whether it's a round cascader
   */
  round: Boolean,
  searchOnPanel: Boolean, // 是否将搜索框放到面板上
  searchPlaceholder: {
    type: String,
    default: '请输入',
  },
  highlightMatchText: {
    type: Boolean,
    default: true,
  },
  /**
   * @description 是否可以展开被禁用的节点
   */
  expandDisabled: {
    type: Boolean,
    default: false,
  },
  surface: {
    type: Boolean,
  },
  /**
   * @description 选中项回填的方式
   */
  showCheckedStrategy: {
    type: definePropType<CascaderStrategy>(String),
    default: CascaderStrategy.SHOW_CHILD,
  },
  /**
   * @description 最大折叠的tag数量
   */
  maxCollapseTags: {
    type: [Number, String],
    default: 1,
  },
   /**
   * @description 最大数量
   */
  multipleLimit: {
    type: [Number],
    default: 0,
  },
})

export const cascaderEmits = {
  [UPDATE_MODEL_EVENT]: (val: CascaderValue) => !!val || val === null,
  [CHANGE_EVENT]: (val: CascaderValue) => !!val || val === null,
  focus: (evt: FocusEvent) => evt instanceof FocusEvent,
  blur: (evt: FocusEvent) => evt instanceof FocusEvent,
  visibleChange: (val: boolean) => isBoolean(val),
  expandChange: (val: CascaderValue) => !!val,
  removeTag: (val: CascaderNode['valueByOption']) => !!val,
  checkedChange: (val: CascaderValue) => !!val,
}

// Type name is taken(cascader-panel/src/node), needs discussion
// export type CascaderProps = ExtractPropTypes<typeof cascaderProps>

export type CascaderEmits = typeof cascaderEmits
